import { FLOW_GRAPH, IChatMessage } from 'ai-assistant/models/chat-message';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';

import './AssistantMessage.light.scss';
import './AssistantMessage.dark.scss';
import useAssistantChat, { AssistantChatStatus } from 'ai-assistant/hooks/useAssistantChat';
import { Icon } from '@client:common/icon';
import { UUID } from 'crypto';
import { AssistantMessageFeedback } from './assistant-message-feedback/AssistantMessageFeedback';

interface Props {
  message: IChatMessage;
  chat: ReturnType<typeof useAssistantChat>;
  isMostRecentMessage: boolean;
}

export function AssistantMessage({
  message,
  chat,
  isMostRecentMessage
}: Props) {

  const handleFlowSelection = (flowId: UUID, selectionIndex: number) => {
    chat.coordinator.withStagedFlowMessage(selectionIndex, async (nextMessageId) => {
      await chat.handleFlowSelection(flowId, selectionIndex, nextMessageId);
    })
  }

  if (message.role !== 'user' && isMostRecentMessage && chat.status === AssistantChatStatus.SUBMITTED) {
    return (
      <p className='assistant-message__loading-text assistant-message__loading-text-base'>One moment please...</p>
    )
  }

  return (
    <div className={`assistant-message assistant-message--${message.role}`}>
      {message.role === 'user' ? (
        <motion.div
          layoutId={chat.coordinator.stagedFlowMessageIndex !== null ? `flow-message-${chat.coordinator.stagedFlowMessageIndex}` : isMostRecentMessage ? `message-container-${message.id}` : null}
          className='assistant-message__content'
          style={{
            borderRadius: '12px 12px 0px 12px',
          }}
          transition={{
            duration: 0.5,
            ease: 'easeOut'
          }}
          onLayoutAnimationComplete={chat.coordinator.stagedFlowMessageIndex !== null ? chat.coordinator.handleFlowMessageAnimationFinish : chat.coordinator.handleUserMessageAnimationFinish}
        >
          <p>{message.content}</p>
        </motion.div>
      ) : (
        <>
          <div className='assistant-message__content'>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
          {message.role !== 'system' && (
            <AssistantMessageFeedback chat={chat} messageId={message.id} />
          )}
        </>
      )}

      {isMostRecentMessage && message.type === 'flow' && message.role === 'assistant' && (
        <div className='assistant-message__flow'>
          {FLOW_GRAPH[message.flowId].options?.map((option, index) => (
            <motion.div
              layoutId={`flow-message-${index}`}
              key={index}
              className='assistant-message__flow-option'
              onClick={() => handleFlowSelection(message.flowId, index)}
              whileHover={{
                scale: 1.02,
              }}
              style={{ borderRadius: '12px' }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }}
            >
              <p>{option.text}</p>
              <Icon icon="send" />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
